import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useToggle } from 'react-use';
import SimpleModal from 'components/SimpleModal';
import { selectTeamHash } from 'selectors';
import { reactivateTeam as _reactivateTeam } from 'thunk';
import { OnSuccessInstance, OnSuccessV2 } from 'types';
import { Box } from '@mui/material';
import noop from 'lodash/noop';

const modalPropsDefault = {
  teamIds: [],
  onSuccess: noop,
  skipStateUpdate: false,
};

type ModalPropsType = {
  teamIds: number[];
  onSuccess?: OnSuccessV2;
};

const useReactivateTeamsConfirmModal = () => {
  const [isModalOpen, toggleModal] = useToggle(false);
  const [{ teamIds, onSuccess }, setModalProps] = useState<ModalPropsType>(modalPropsDefault);
  const dispatch = useAppDispatch();
  const teamHash = useAppSelector(selectTeamHash);

  const onClose = useCallback(() => {
    toggleModal(false);
  }, [toggleModal]);

  const reactivateTeam = useCallback(
    ({ teamIds, onSuccess }: { teamIds: number[]; onSuccess?: OnSuccessV2 }) => {
      dispatch(_reactivateTeam({ teamIds, onSuccess }));
    },
    [dispatch],
  );

  const onConfirm = useCallback(() => {
    reactivateTeam({ teamIds, onSuccess });
    toggleModal(false);
  }, [reactivateTeam, teamIds, onSuccess, toggleModal]);

  const handleOpenReactivateTeamsModal = useCallback(
    (nextModalProps: ModalPropsType) => {
      setModalProps(nextModalProps);
      toggleModal(true);
    },
    [toggleModal],
  );

  const ModalComponent = isModalOpen && (
    <SimpleModal
      open
      onClose={onClose}
      onConfirm={onConfirm}
      headerText="Teams Reactivation"
      confirmButtonText="Reactivate"
      onConfirmButtonClassNames="blue-warning-button"
      Body={
        <div>
          <div>
            You are about to reactivate <b>{teamIds.length}</b>{' '}
            {teamIds.length > 1 ? 'Teams' : 'Team'}.
          </div>
          <Box
            component="ul"
            sx={{
              maxHeight: 350,
              overflow: 'auto',
            }}
          >
            {teamIds.map((id) => {
              const team = teamHash[id];
              return <li key={id}>{team?.name || 'N/A'}</li>;
            })}
          </Box>
        </div>
      }
    />
  );

  return {
    ReactivateTeamsConfirmModal: ModalComponent,
    handleOpenReactivateTeamsModal,
    reactivateTeam,
  };
};

export default useReactivateTeamsConfirmModal;
